import { useCallback, useEffect, useState } from 'react';
import { callRpc } from '@/lib/supabaseTyped';

export interface WorkflowAuditEntry {
  id: string;
  workflow_id: string;
  actor_id: string | null;
  /**
   * True when the actor user has been erased (GDPR right-to-be-forgotten).
   * The UI should render a `[redacted]` label instead of looking up the
   * email via `actorEmails` — the server returns `null` email for
   * redacted rows so stale PII never leaks client-side.
   */
  actor_redacted: boolean;
  action: 'insert' | 'update' | 'delete';
  before_hash: string | null;
  after_hash: string | null;
  summary: string | null;
  created_at: string;
}

interface WorkflowAuditRpcRow extends WorkflowAuditEntry {
  actor_email: string | null;
}

interface UseAuditLogReturn {
  entries: WorkflowAuditEntry[];
  loading: boolean;
  refresh: () => void;
  actorEmails: Record<string, string>;
}

/**
 * Read the audit trail for a workflow. Uses `get_audit_log_with_actors`
 * RPC (SECURITY DEFINER, see migration 20260421130000) which joins
 * `workflow_audit_log` with `profiles` server-side and reapplies the
 * same owner-or-coop-member authorization check that RLS enforces on
 * the underlying table. One round-trip instead of two.
 */
export function useWorkflowAuditLog(workflowId: string | null): UseAuditLogReturn {
  const [entries, setEntries] = useState<WorkflowAuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [actorEmails, setActorEmails] = useState<Record<string, string>>({});

  const fetchLog = useCallback(async () => {
    if (!workflowId) {
      setEntries([]);
      setActorEmails({});
      return;
    }
    setLoading(true);
    try {
      const { data } = await callRpc<WorkflowAuditRpcRow[]>('get_audit_log_with_actors', {
        _workflow_id: workflowId,
        _limit: 100,
      });
      const rows = data ?? [];
      // Normalise: older RPC versions (pre-20260421140000) don't return
      // `actor_redacted`. Default to false so rendering is unchanged for
      // legacy deployments that haven't applied the migration yet.
      const normalised = rows.map(r => ({ ...r, actor_redacted: r.actor_redacted ?? false }));
      setEntries(normalised);

      const map: Record<string, string> = {};
      for (const row of normalised) {
        // Skip redacted actors — their email is scrubbed server-side.
        if (row.actor_redacted) continue;
        if (row.actor_id && row.actor_email) map[row.actor_id] = row.actor_email;
      }
      setActorEmails(map);
    } catch {
      setEntries([]);
      setActorEmails({});
    }
    setLoading(false);
  }, [workflowId]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  return { entries, loading, refresh: fetchLog, actorEmails };
}
